import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { Button } from 'antd';
import styled from 'styled-components';

import { getCmiDigestAdminUsers } from 'apis/UserAPI';
import Notification from 'components/Notification/Notification';
import { useDebounce } from 'utils/hooks/useDebounce';

import { CmiDigestAdminUser } from '../types';
import CmiDigestAdminList from './CmiDigestAdminList';
import CmiDigestAdminToolbar from './CmiDigestAdminToolbar';
import CmiDigestBulkUserModal from './CmiDigestBulkUserModal';
import CmiDigestUserModal from './CmiDigestUserModal';

const Container = styled.div`
  padding-bottom: 30px;
  width: 100%;
  background-color: white;
`;

const ToolbarContainer = styled.div`
  padding: 15px 30px 0;
`;

const StyledEditButton = styled(Button)`
  background-color: var(--button-blue);
  border-color: var(--button-blue);
  padding: 10px 30px;
  height: 40px;
  font-family: var(--font-regular);
  border-radius: 4px;

  &:hover {
    background-color: var(--button-blue-hover) !important;
  }
`;

const PAGE_SIZE = 10;

export const CMI_DIGEST_ADMIN_USERS_QUERY_KEY = 'cmiDigestAdminUsers';

const CmiDigestAdmin = () => {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [companies, setCompanies] = useState<number[]>([]);
  const [community, setCommunity] = useState<number | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [isMultipleSelection, setMultipleSelection] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<CmiDigestAdminUser[]>([]);
  const [editingUser, setEditingUser] = useState<CmiDigestAdminUser | null>(null);
  const [isBulkEditOpen, setBulkEditOpen] = useState(false);

  const debouncedSearch = useDebounce(searchTerm);

  const { data, isFetching } = useQuery(
    [CMI_DIGEST_ADMIN_USERS_QUERY_KEY, page, debouncedSearch, companies, community, status],
    () =>
      getCmiDigestAdminUsers({
        page,
        page_size: PAGE_SIZE,
        search: debouncedSearch,
        companies,
        communities: community !== null ? [community] : [],
        status: status === null ? undefined : status,
      }),
    { keepPreviousData: true }
  );

  const users: CmiDigestAdminUser[] = data?.results ?? [];
  const total: number = data?.count ?? 0;

  const refetchUsers = () => queryClient.invalidateQueries(CMI_DIGEST_ADMIN_USERS_QUERY_KEY);

  const resetSelection = () => {
    setSelectedUsers([]);
    setMultipleSelection(false);
  };

  const toggleOneUser = (user: CmiDigestAdminUser) => {
    setSelectedUsers((prev) =>
      prev.some((selected) => selected.id === user.id)
        ? prev.filter((selected) => selected.id !== user.id)
        : [...prev, user]
    );
  };

  const allSelected =
    users.length > 0 && users.every((user) => selectedUsers.some((selected) => selected.id === user.id));

  const toggleAllUsers = () => {
    if (allSelected) {
      setSelectedUsers((prev) => prev.filter((selected) => !users.some((user) => user.id === selected.id)));
    } else {
      setSelectedUsers((prev) => {
        const merged = [...prev];
        users.forEach((user) => {
          if (!merged.some((selected) => selected.id === user.id)) {
            merged.push(user);
          }
        });
        return merged;
      });
    }
  };

  const handleRowClick = (user: CmiDigestAdminUser) => {
    if (!isMultipleSelection) {
      setEditingUser(user);
    }
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
  };

  return (
    <Container>
      <Notification
        totalCount={users.length}
        selectedCount={selectedUsers.length}
        singularLabel="user"
        multipleLabel="users"
        onClose={resetSelection}
        actions={
          <StyledEditButton
            type="primary"
            onClick={() => setBulkEditOpen(true)}
            data-test-id="cmi-digest-admin-edit-multiple-btn"
          >
            Edit Multiple
          </StyledEditButton>
        }
      />
      <ToolbarContainer>
        <CmiDigestAdminToolbar
          isMultipleSelection={isMultipleSelection}
          toggleMultipleSelection={() => {
            setMultipleSelection((prev) => !prev);
            setSelectedUsers([]);
          }}
          companies={companies}
          setCompanies={(value) => {
            setCompanies(value);
            setPage(1);
          }}
          community={community}
          setCommunity={(value) => {
            setCommunity(value);
            setPage(1);
          }}
          status={status}
          setStatus={(value) => {
            setStatus(value);
            setPage(1);
          }}
          searchTerm={searchTerm}
          setSearchTerm={(value) => {
            setSearchTerm(value);
            setPage(1);
          }}
        />
      </ToolbarContainer>
      <CmiDigestAdminList
        users={users}
        loading={isFetching}
        isMultipleSelection={isMultipleSelection}
        selectedIds={selectedUsers.map((user) => user.id)}
        allSelected={allSelected}
        onToggleAll={toggleAllUsers}
        onToggleSelect={toggleOneUser}
        onRowClick={handleRowClick}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={handlePageChange}
      />
      {editingUser && (
        <CmiDigestUserModal
          user={editingUser}
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => {
            setEditingUser(null);
            refetchUsers();
          }}
        />
      )}
      {isBulkEditOpen && (
        <CmiDigestBulkUserModal
          users={selectedUsers}
          isOpen={isBulkEditOpen}
          onClose={() => setBulkEditOpen(false)}
          onSaved={() => {
            setBulkEditOpen(false);
            resetSelection();
            refetchUsers();
          }}
        />
      )}
    </Container>
  );
};

export default CmiDigestAdmin;
